import * as z from "zod/mini";
import { MAX_VOICE_SIGNAL_CHARS, clientMessage, frameFits, serverMessage } from "./messages.js";
import type { ClientMessage, ServerMessage } from "./messages.js";

export type ParseError = "binary" | "too_large" | "not_json" | "invalid";
export type ParseResult<M> = { ok: true; msg: M } | { ok: false; error: ParseError };

/** Size cap → JSON.parse → schema. Never throws, whatever `raw` is. */
export function parseJson<S extends z.ZodMiniType>(
  schema: S,
  raw: unknown,
  maxChars: number,
): ParseResult<z.infer<S>> {
  if (typeof raw !== "string") return { ok: false, error: "binary" };
  if (raw.length > maxChars) return { ok: false, error: "too_large" };
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, error: "not_json" };
  }
  const result = z.safeParse(schema, json);
  return result.success ? { ok: true, msg: result.data } : { ok: false, error: "invalid" };
}

/**
 * Server side: run on EVERY inbound frame after the handshake. Clients send text frames only in v1,
 * so a binary frame is an error. Never throws.
 */
export function parseClientMessage(raw: string | ArrayBuffer): ParseResult<ClientMessage> {
  // Voice signals may be longer than other messages (frameFits); nothing else may.
  if (typeof raw === "string" && !frameFits(raw)) return { ok: false, error: "too_large" };
  return parseJson(clientMessage, raw, MAX_VOICE_SIGNAL_CHARS);
}

/**
 * Client side, for text frames. Server output is trusted, but checking it costs little at
 * control-message rates and turns version skew into a clear error. Binary frames: decodeServerFrame.
 */
export function parseServerMessage(raw: string): ParseResult<ServerMessage> {
  return parseJson(serverMessage, raw, Number.POSITIVE_INFINITY);
}

export function encodeMessage(msg: ClientMessage | ServerMessage): string {
  return JSON.stringify(msg);
}
